import React, { useEffect } from 'react';
import { Form, Input, InputNumber, Switch } from 'antd';
import { LocalizedContentTabs, type LocalizedFieldConfig } from '../../components/LocalizedContentTabs';

/** The chip label is localised; everything else is language-neutral. */
export const PET_SKIN_CATEGORY_LANG_FIELDS: LocalizedFieldConfig[] = [
  { key: 'name', label: 'Name', required: true },
];

/** Mirrors the API's `slugify`: "HK Local Delicacies" → "hk-local-delicacies". */
const slugify = (label: string) =>
  label
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, 60)
    .replace(/^-+|-+$/g, '');

export const PetSkinCategoryFormFields: React.FC<{ slugEditable: boolean }> = ({
  slugEditable,
}) => {
  const form = Form.useFormInstance();
  const name: string | undefined = Form.useWatch('name', form);

  // Follow the English name until the editor types a slug of their own.
  useEffect(() => {
    if (!slugEditable || form.isFieldTouched('slug')) return;
    form.setFieldValue('slug', slugify(name ?? ''));
  }, [name, slugEditable, form]);

  return (
    <>
      <LocalizedContentTabs fields={PET_SKIN_CATEGORY_LANG_FIELDS} />

      <Form.Item
        label="Slug"
        name="slug"
        rules={
          slugEditable
            ? [
                {
                  pattern: /^[a-z0-9]+(-[a-z0-9]+)*$/,
                  message: 'Lowercase kebab-case only, e.g. desserts',
                },
              ]
            : []
        }
        extra={
          slugEditable
            ? 'Filled in from the English name; edit it if you want. Left empty, the server generates one. It cannot be changed later.'
            : 'Fixed — skins and the app refer to this id.'
        }
      >
        <Input disabled={!slugEditable} placeholder="desserts" />
      </Form.Item>

      <Form.Item label="Sort order" name="sortOrder" extra="Chip order, after All.">
        <InputNumber style={{ width: '100%' }} min={0} precision={0} />
      </Form.Item>

      <Form.Item
        label="Active"
        name="isActive"
        valuePropName="checked"
        extra="An inactive chip is hidden; its skins still show under All."
      >
        <Switch />
      </Form.Item>
    </>
  );
};
